import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { API_URL } from '../../config/api'
import {
  isCustomerAccount,
  isSessionExpired,
  setLastActivityAt,
  clearLastActivityAt,
} from '../../utils/sessionTimeout'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  kycStatus: 'pending' | 'reviewing' | 'approved' | 'rejected'
  accountType: 'basic' | 'premium' | 'vip' | 'admin'
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
}

// 初始化时从 localStorage 同步读取认证状态
const getInitialState = (): AuthState => {
  const token = localStorage.getItem('token')
  const userStr = localStorage.getItem('user')
  
  if (token && userStr) {
    try {
      const user = JSON.parse(userStr)

      if (isCustomerAccount(user.accountType) && isSessionExpired()) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        clearLastActivityAt()
      } else {
        return {
          isAuthenticated: true,
          user,
          token,
          loading: false,
          error: null,
        }
      }
    } catch {
      // JSON 解析失败，清除无效数据
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      clearLastActivityAt()
    }
  }
  
  return {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: false,
    error: null,
  }
}

const initialState: AuthState = getInitialState()

// 异步thunk用于检查认证状态
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      
      if (!token || !userStr) {
        throw new Error('No authentication data found')
      }

      // 先返回本地数据，然后在后台验证并更新
      const localUser = JSON.parse(userStr)
      
      // 异步验证 token 并获取最新用户信息
      fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => response.json())
        .then(data => {
          if (data.success && data.data.user) {
            // 更新 localStorage 中的用户信息
            const updatedUser = {
              ...localUser,
              kycStatus: data.data.user.kycStatus,
              accountType: data.data.user.accountType,
              name: data.data.user.name
            }
            localStorage.setItem('user', JSON.stringify(updatedUser))
            
            // 触发一个自定义事件来通知其他组件更新
            window.dispatchEvent(new CustomEvent('userUpdated', { detail: updatedUser }))
          }
        })
        .catch(error => {
          console.error('Failed to verify token:', error)
        })

      return { user: localUser, token }
    } catch (error) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      return rejectWithValue('Authentication failed')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.error = null
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.loading = false
      state.isAuthenticated = true
      state.user = action.payload.user
      state.token = action.payload.token
      state.error = null
      
      // 保存到localStorage
      localStorage.setItem('token', action.payload.token)
      localStorage.setItem('user', JSON.stringify(action.payload.user))
      if (isCustomerAccount(action.payload.user.accountType)) {
        setLastActivityAt()
      }
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.error = action.payload
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.error = null
      
      // 清除localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      clearLastActivityAt()
    },
    clearError: (state) => {
      state.error = null
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuth.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.error = null
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.loading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.error = action.payload as string
      })
  },
})

export const { loginStart, loginSuccess, loginFailure, logout, clearError, updateUser } = authSlice.actions
export default authSlice.reducer